import { Injectable } from '@angular/core';
import {Observable, of, throwError} from 'rxjs';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from '@angular/common/http';
import {catchError, first, map, switchMap, tap} from 'rxjs/operators';
import { apiUrl } from '../../../environments/environment';
import {AuthService} from '../auth/auth.service';
import {StreamProgressEvent} from '../interfaces/fileUploadInterfaces';

export interface GenerateEmbeddingsResponse {
  message: string;
  items_checked: number;
  embeddings_generated: number;
  errors_encountered: number;
}
// Interface for snippet information from backend
export interface SnippetInfo {
  snippet_text: string;
  snippet_id: string;
  related_ki_count: number;
  related_ki_ids: string[];
}

export interface IndexFileResponse {
  chunks_created: number;
  message: string;
}

// Response model for file snippets endpoint
export interface FileSnippetsResponse {
  snippets: SnippetInfo[];
  total_count: number;
  file_id: string;
}

// Corresponds to FileRecordCreate Pydantic model
export interface FileRecordCreatePayload {
  name: string;
  userName: string;
  projectName: string;
  content: string; // The core string content is required on creation
  contentType?: string; // Simple string type hint (e.g., "text", "html")
}

// Corresponds to FileRecordUpdate Pydantic model
export interface FileRecordUpdatePayload {
  name?: string;
  content?: string;
  contentType?: string;
  // Add status/message if you add them to the Pydantic model later
  // status?: string;
  // message?: string;
}

// Corresponds to FileRecordResponse Pydantic model
export interface FileRecordResponse {
  _id: string; // Mapped from _id
  name: string;
  userName: string;
  projectName: string;
  contentType?: string;
  timestamp: string; // ISO 8601 string from backend
  content?: string; // Content is optional (e.g., excluded in list view)
}

// Request interface for knowledge extraction
export interface ExtractKnowledgeRequest {
  document_content: string;
  model: string; // The model to use for extraction
}

// Response interface for knowledge snippets
export interface SnippetsResponse {
  snippets: string[];
  token_usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

// Add these interfaces to your existing interfaces

// Request interface for snippet to KI conversion
export interface SnippetToKIRequest {
  snippet_text: string;
  file_id: string;
  model: string;
  projectName: string;
  userName: string;
}

export interface KBSearchResult {
  item: KnowledgeBaseItem;
  score: number;
}

// Define the interface for the data points coming from the backend for the vector space view
export interface KnowledgeSpacePoint {
  id: string;
  title: string;
  x: number;
  y: number;
  snippet?: string;
  // Add other metadata if needed
}

// Knowledge Base Item structure
/**
 * Interface representing a Knowledge Base item, aligned with the backend Pydantic model
 */
export interface KnowledgeBaseItem {
  _id: string;
  id?: string; // Some places may use this instead of _id
  kb_id: string;
  title: string;
  term: string;
  definition: string;
  projectName: string;
  userName: string;
  createdAt: string;
  lastModified: string;

  // Optional fields
  examples?: string[];
  related_terms?: string[];
  scope?: string;
  type?: string;

  // Source information fields
  source_type?: string;
  source_file_id?: string;
  source_file_name?: string;
  source_snippet?: string;
  source_story_id?: string;
  original_ambiguity_id?: string;

  // G4: Structured fields for ambiguity-based KIs
  question?: string;
  impact_text?: string;
  answer?: string;
  source?: string;              // e.g. "user_manual"
  story_debug_ref?: string;     // Reference to story_debug_log entry

  // Embedding fields
  embedding?: number[];  // The actual vector (if needed)
  embedding_model?: string;
  embedding_dimension?: number;
  embedding_task_type?: string;
  embedding_version?: number;
  embedding_text?: string;
}

// Response from the snippet-to-ki endpoint
export interface KICreationResponse {
  created_items: KnowledgeBaseItem[];
  token_usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

export interface NeighborResponse {
  neighbor_ids: string[];
}

export interface ClusterItemInfo {
  id: string;
  title: string;
  x: number; // <-- ADDED
  y: number; // <-- ADDED
}

export interface ClusterDefinition {
  cluster_id: number;
  size: number;
  items: ClusterItemInfo[]; // Uses updated ClusterItemInfo
}

export interface ClusterReportResponse {
  clusters: ClusterDefinition[];
  noise_points: ClusterItemInfo[]; // Uses updated ClusterItemInfo
}

@Injectable({
  providedIn: 'root'
})
export class PersistenceApiService {
  private apiUrl = apiUrl; // Use the base API URL from environment

  constructor(
    private http: HttpClient,
    private authService: AuthService // Inject AuthService to get tokens
    // Inject other services like StatoSessioneService if needed for token tracking
  ) {}

  /**
   * Gets the authorization headers with the current Bearer token.
   * Mirrors the implementation in UserStoryService.
   */
  private getHeaders(): Observable<HttpHeaders> {
    return this.authService.token$.pipe(
      switchMap(token => {
        if (!token) {
          // Handle case where token is not available (e.g., redirect to login)
          console.error('Authentication token is missing.');
          return throwError(new Error('Authentication token is missing. Please log in.'));
        }
        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
        // Wrap in new Observable to fit switchMap structure easily
        return new Observable<HttpHeaders>(observer => {
          observer.next(headers);
          observer.complete();
        });
      }),
      catchError(err => {
        // Catch errors during token retrieval itself
        console.error('Error getting authentication token:', err);
        return throwError(new Error('Failed to retrieve authentication token.'));
      })
    );
  }

  /**
   * Creates a new file record in the backend persistence layer.
   * Corresponds to POST /files/
   * @param payload - The data for the new file record, including content.
   * @returns Observable of the created FileRecordResponse.
   */
  createFileRecord(payload: FileRecordCreatePayload): Observable<FileRecordResponse> {
    return this.getHeaders().pipe(
      switchMap((headers) => {
        const url = `${this.apiUrl}/files/`;
        console.log('PersistenceApiService: Creating file record at', url, 'with payload:', {
          ...payload, // Log payload but maybe truncate content
          content: payload.content.substring(0, 100) + (payload.content.length > 100 ? '...' : '')
        });
        return this.http.post<FileRecordResponse>(url, payload, { headers });
      }),
      tap(response => {
        console.log('PersistenceApiService: File record created successfully:', response);
      }),
      catchError(this.handleError) // Use the shared error handler
    );
  }

  /**
   * Lists file records for a specific user and project.
   * Corresponds to GET /files/
   * @param userName - The username of the file owner.
   * @param projectName - The project name the files belong to.
   * @returns Observable of an array of FileRecordResponse (content likely excluded by backend).
   */
  listFiles(userName: string, projectName: string): Observable<FileRecordResponse[]> {
    return this.getHeaders().pipe(
      switchMap((headers) => {
        const url = `${this.apiUrl}/files/`;
        const params = new HttpParams()
          .set('userName', userName)
          .set('projectName', projectName);
        console.log('PersistenceApiService: Listing file records from', url, 'with params:', { userName, projectName });
        return this.http.get<FileRecordResponse[]>(url, { headers, params });
      }),
      tap(response => {
        console.log(`PersistenceApiService: Retrieved ${response.length} file records.`);
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves a specific file record by its ID, including content.
   * Corresponds to GET /files/{file_id}
   * @param fileId - The unique ID (_id) of the file record.
   * @param userName - The username for authorization check.
   * @param projectName - The project name for authorization check.
   * @returns Observable of the specific FileRecordResponse.
   */
  getFileRecord(fileId: string, userName: string, projectName: string): Observable<FileRecordResponse> {
    return this.getHeaders().pipe(
      switchMap((headers) => {
        const url = `${this.apiUrl}/files/${fileId}`;
        const params = new HttpParams()
          .set('userName', userName)
          .set('projectName', projectName);
        console.log('PersistenceApiService: Getting file record from', url, 'with params:', { fileId, userName, projectName });
        return this.http.get<FileRecordResponse>(url, { headers, params });
      }),
      tap(response => {
        console.log('PersistenceApiService: Retrieved file record:', response._id);
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Updates specific fields of an existing file record.
   * Corresponds to PATCH /files/{file_id}
   * @param fileId - The unique ID (_id) of the file record to update.
   * @param userName - The username for authorization check.
   * @param projectName - The project name for authorization check.
   * @param payload - An object containing the fields to update.
   * @returns Observable of the updated FileRecordResponse.
   */
  updateFileRecord(fileId: string, userName: string, projectName: string, payload: FileRecordUpdatePayload): Observable<FileRecordResponse> {
    return this.getHeaders().pipe(
      switchMap((headers) => {
        const url = `${this.apiUrl}/files/${fileId}`;
        const params = new HttpParams()
          .set('userName', userName)
          .set('projectName', projectName);
        console.log('PersistenceApiService: Updating file record at', url, 'with payload:', {
          ...payload, // Log payload but maybe truncate content if present
          content: payload.content ? payload.content.substring(0, 100) + (payload.content.length > 100 ? '...' : '') : undefined
        });
        return this.http.patch<FileRecordResponse>(url, payload, { headers, params });
      }),
      tap(response => {
        console.log('PersistenceApiService: File record updated successfully:', response._id);
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Deletes a specific file record by its ID.
   * Corresponds to DELETE /files/{file_id}
   * @param fileId - The unique ID (_id) of the file record to delete.
   * @param userName - The username for authorization check.
   * @param projectName - The project name for authorization check.
   * @returns Observable<{ message: string }> containing the success message from the backend.
   */
  deleteFileRecord(fileId: string, userName: string, projectName: string): Observable<{ message: string }> {
    return this.getHeaders().pipe(
      switchMap((headers) => {
        const url = `${this.apiUrl}/files/${fileId}`;
        const params = new HttpParams()
          .set('userName', userName)
          .set('projectName', projectName);
        console.log('PersistenceApiService: Deleting file record at', url, 'with params:', { fileId, userName, projectName });
        // Expecting a simple message object like { "message": "..." }
        return this.http.delete<{ message: string }>(url, { headers, params });
      }),
      tap(response => {
        console.log('PersistenceApiService: File record deleted successfully:', response.message);
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Triggers the deterministic chunking and indexing of a file.
   * Corresponds to POST /index-file-chunks
   */
  indexFileChunks(fileId: string, userName: string, projectName: string): Observable<IndexFileResponse> {
    return this.getHeaders().pipe(
      switchMap((headers) => {
        const url = `${this.apiUrl}/index-file-chunks`;
        const payload = {
          file_id: fileId,
          userName: userName,
          projectName: projectName
        };

        console.log('PersistenceApiService: Indexing file chunks:', payload);
        return this.http.post<IndexFileResponse>(url, payload, { headers });
      }),
      tap(response => {
        console.log(`PersistenceApiService: Indexed ${response.chunks_created} chunks.`);
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Same as indexFileChunks but with NDJSON streaming progress.
   * Emits StreamProgressEvent for each progress/complete/error line.
   */
  indexFileChunksStream(fileId: string, userName: string, projectName: string): Observable<StreamProgressEvent> {
    return this.authService.token$.pipe(
      first(),
      switchMap(token => {
        return new Observable<StreamProgressEvent>(observer => {
          const url = `${this.apiUrl}/index-file-chunks-stream`;
          const payload = { file_id: fileId, userName, projectName };
          const abortController = new AbortController();

          fetch(url, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
            signal: abortController.signal,
          }).then(async response => {
            if (!response.ok) {
              observer.error(new Error(`Indexing failed: ${response.status}`));
              return;
            }
            const reader = response.body!.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() || '';
              for (const line of lines) {
                if (line.trim()) {
                  try {
                    observer.next(JSON.parse(line));
                  } catch { /* skip */ }
                }
              }
            }
            if (buffer.trim()) {
              try { observer.next(JSON.parse(buffer)); } catch { /* skip */ }
            }
            observer.complete();
          }).catch(err => {
            if (err.name !== 'AbortError') observer.error(err);
          });

          return () => abortController.abort();
        });
      })
    );
  }

  /**
   * Converts multiple text snippets into Knowledge Items with cross-snippet consolidation.
   * Corresponds to POST /snippets-to-ki
   * @param snippets - Array of text snippets to analyze
   * @param fileId - ID of the source file
   * @param userName - Username of the owner
   * @param projectName - Project name
   * @param language
   * @param model - The LLM model to use for analysis
   * @returns Observable of the KICreationResponse containing created Knowledge Items
   */
  convertSnippetsBatchToKI(
    snippets: string[],
    fileId: string,
    userName: string,
    projectName: string,
    language: string,
    model: string = 'default'
  ): Observable<KICreationResponse> {
    return this.getHeaders().pipe(
      switchMap((headers) => {
        const url = `${this.apiUrl}/snippets-to-ki`;
        const payload = {
          snippets: snippets,
          file_id: fileId,
          model: model,
          language: language,
          projectName: projectName,
          userName: userName
        };

        console.log('PersistenceApiService: Converting batch of snippets to KI:', {
          ...payload,
          snippets: `${snippets.length} snippets`
        });

        return this.http.post<KICreationResponse>(url, payload, { headers });
      }),
      tap(response => {
        console.log(`PersistenceApiService: Successfully created ${response.created_items.length} Knowledge Items.`);
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Retrieves all Knowledge Items associated with a specific file.
   * Corresponds to GET /file-kis/{file_id}
   * @param fileId - The ID of the file to get KIs for
   * @param userName - The username for authorization check
   * @param projectName - The project name for authorization check
   * @returns Observable of an array of KnowledgeBaseItem objects
   */
  getKnowledgeItemsByFile(fileId: string, userName: string, projectName: string): Observable<KnowledgeBaseItem[]> {
    return this.getHeaders().pipe(
      switchMap((headers) => {
        const url = `${this.apiUrl}/file-kis/${fileId}`;
        // Add query parameters for authorization context
        const params = new HttpParams()
          .set('userName', userName)
          .set('projectName', projectName);

        console.log('PersistenceApiService: Getting Knowledge Items for file:', fileId);

        return this.http.get<KnowledgeBaseItem[]>(url, { headers, params });
      }),
      tap(response => {
        console.log(`PersistenceApiService: Retrieved ${response.length} Knowledge Items for file ${fileId}.`);
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Fetches all knowledge snippets associated with a specific file
   * Corresponds to GET /file-snippets/{file_id}
   * @param fileId The ID of the file to retrieve snippets for
   * @param userName The username for authorization check
   * @param projectName The project name for authorization check
   * @returns Observable of FileSnippetsResponse containing snippet information
   */
  getFileSnippets(fileId: string, userName: string, projectName: string): Observable<FileSnippetsResponse> {
    return this.getHeaders().pipe(
      switchMap((headers) => {
        const url = `${this.apiUrl}/file-snippets/${fileId}`;
        const params = new HttpParams()
          .set('userName', userName)
          .set('projectName', projectName);

        console.log('PersistenceApiService: Getting snippets for file:', fileId);

        return this.http.get<FileSnippetsResponse>(url, { headers, params });
      }),
      tap(response => {
        console.log(`PersistenceApiService: Retrieved ${response.total_count} snippets for file ${fileId}.`);
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Search the knowledge base using a text query
   * @param query - The search query text
   * @param userName - The username for filtering results
   * @param projectName - The project name to scope the search
   * @param limit - Maximum number of similar items to return (default: 10)
   * @returns Observable of search results containing matching knowledge base items with similarity scores
   */
  searchKnowledgeBase(
    query: string,
    userName: string,
    projectName: string,
    limit: number = 100
  ): Observable<KBSearchResult[]> {  // Updated return type
    return this.getHeaders().pipe(
      switchMap((headers) => {
        const payload = { query, projectName, userName, limit };
        return this.http.post<KBSearchResult[]>(  // Updated type
          `${this.apiUrl}/search/`,
          payload,
          { headers }
        );
      }),
      tap(results => {
        console.log('Knowledge base search results:', results.length);
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Triggers the backend process to generate missing embeddings for
   * knowledge base items associated with the specified user and project.
   * Corresponds to POST /knowledgebase/generate-missing-embeddings/
   *
   * @param userName - The username to filter knowledge base items.
   * @param projectName - The project name to filter knowledge base items.
   * @returns Observable of GenerateEmbeddingsResponse containing a summary of the operation.
   */
  generateMissingEmbeddings(userName: string, projectName: string): Observable<GenerateEmbeddingsResponse> {
    return this.getHeaders().pipe(
      switchMap((headers) => {
        // The endpoint uses POST, but parameters are sent via query params
        const url = `${this.apiUrl}/knowledgebase/generate-missing-embeddings/`;
        const params = new HttpParams()
          .set('userName', userName)
          .set('projectName', projectName);

        console.log(`PersistenceApiService: Triggering missing embedding generation at ${url} for:`, { userName, projectName });

        // Make a POST request. Since data is in query params, the body is null.
        return this.http.post<GenerateEmbeddingsResponse>(url, null, { headers, params });
      }),
      tap(response => {
        console.log('PersistenceApiService: Missing embedding generation response:', response);
        // You might want to show a notification based on the response message or counts
        if (response.embeddings_generated > 0) {
          console.log(`Successfully generated ${response.embeddings_generated} embeddings.`);
        } else if (response.items_checked > 0) {
          console.log('Checked for missing embeddings, none needed generation.');
        } else {
          console.log('No items found requiring embedding generation for this user/project.');
        }
        if (response.errors_encountered > 0) {
          console.warn(`Encountered ${response.errors_encountered} errors during embedding generation.`);
        }
      }),
      catchError(this.handleError) // Reuse the shared error handler
    );
  }

  /**
   * Fetch knowledge space data points for visualization.
   * Can be filtered by a specific file ID.
   * @param projectName - The project name.
   * @param fileId - Optional: The ID (_id) of the file to filter by.
   * @returns Observable of knowledge space points.
   */
  getKnowledgeSpace(projectName: string, fileId?: string | null): Observable<KnowledgeSpacePoint[]> {
    // Ensure projectName is valid
    if (!projectName) {
      return throwError(new Error('Project name is required for getKnowledgeSpace.'));
    }

    return this.getHeaders().pipe(
      switchMap(headers => {
        // *** Construct the correct URL based on your FastAPI router setup ***
        // Example: If router prefix is /visualize
        const visualizeUrl = `${this.apiUrl}/knowledge-space/${projectName}`;
        // Example: If router prefix is /kb and endpoint is under it
        // const visualizeUrl = `${this.apiUrl}/kb/visualize/knowledge-space/${projectName}`;

        // --- Set up query parameters ---
        let params = new HttpParams();
        if (fileId) {
          // The backend expects 'file_id' as the query parameter name
          params = params.set('file_id', fileId);
          console.log(`Service: Adding file_id parameter: ${fileId}`);
        }
        // -----------------------------

        console.log(`Service: Calling GET ${visualizeUrl} with params:`, params.toString());
        // Make the GET request with headers and potentially params
        return this.http.get<KnowledgeSpacePoint[]>(visualizeUrl, { headers, params });
      }),
      tap(data => console.log(`Service: Fetched ${data?.length ?? 0} points for knowledge space (File Filter: ${fileId || 'None'}).`)),
      catchError(this.handleError) // Use your service's error handler
    );
  }

  // **** METHOD for fetching neighbors ****
  getNearestNeighbors(
    itemId: string,
    projectName: string,
    k: number = 5,
    fileId?: string | null
  ): Observable<string[]> { // Explicitly type the return value
    return this.getHeaders().pipe(
      switchMap(headers => {
        const neighborsUrl = `${this.apiUrl}/neighbors/${itemId}`;
        let params = new HttpParams()
          // CORRECTED PARAMETER NAME HERE:
          .set('project_name', projectName) // Use snake_case to match FastAPI
          .set('k', k.toString());
        if (fileId) {
          // Also ensure consistency if you use file_id elsewhere
          params = params.set('file_id', fileId); // This one already matches
        }
        console.log('Sending params:', params.toString()); // Add logging for debugging
        return this.http.get<NeighborResponse>(neighborsUrl, { headers, params });
      }),
      map(response => response.neighbor_ids),
      tap(ids => console.log(`Service: Fetched ${ids.length} neighbors for item ${itemId}:`, ids)),
      catchError(err => {
        console.error('Error fetching neighbors:', err); // Log the actual error object
        this.handleError(err);
        console.warn(`Returning empty neighbor list for item ${itemId} due to error.`);
        return of([]);
      })
    );
  }

  /**
   * Fetches cluster analysis report for the knowledge space.
   * Can be filtered by a specific file ID.
   * @param projectName - The project name.
   * @param fileId - Optional: The ID (_id) of the file to filter by.
   * @param clusterParams - Optional: Parameters to pass to the clustering algorithm (e.g., { min_cluster_size: 10 }).
   * @returns Observable of the cluster report response.
   */
  getClusterReport(
    projectName: string,
    fileId?: string | null,
    // Parameters object for backend tuning
    clusterParams?: {
      min_cluster_size?: number;
      min_samples?: number; // Pass null or number
      umap_n_neighbors?: number;
      umap_min_dist?: number;
    }
  ): Observable<ClusterReportResponse> { // Return type uses updated interface

    if (!projectName) {
      return throwError(new Error('Project name is required for getClusterReport.'));
    }

    return this.getHeaders().pipe(
      switchMap(headers => {
        const clusterUrl = `${this.apiUrl}/clusters/${projectName}`; // Adjust if needed
        let params = new HttpParams();

        if (fileId) {
          params = params.set('file_id', fileId);
        }

        // Add clustering/UMAP parameters if provided
        if (clusterParams) {
          if (clusterParams.min_cluster_size !== undefined) {
            params = params.set('min_cluster_size', clusterParams.min_cluster_size.toString());
          }
          // Handle min_samples potentially being null (backend expects int or absent)
          if (clusterParams.min_samples !== undefined && clusterParams.min_samples !== null) {
            params = params.set('min_samples', clusterParams.min_samples.toString());
          }
          if (clusterParams.umap_n_neighbors !== undefined) {
            params = params.set('umap_n_neighbors', clusterParams.umap_n_neighbors.toString());
          }
          if (clusterParams.umap_min_dist !== undefined) {
            params = params.set('umap_min_dist', clusterParams.umap_min_dist.toString());
          }
        }

        console.log(`Service: Calling GET ${clusterUrl} with params:`, params.toString());
        // Expecting the updated ClusterReportResponse structure
        return this.http.get<ClusterReportResponse>(clusterUrl, { headers, params });
      }),
      tap(report => console.log(`Service: Fetched cluster report (File Filter: ${fileId || 'None'}). Found ${report?.clusters?.length ?? 0} clusters and ${report?.noise_points?.length ?? 0} noise points.`)),
      catchError(this.handleError)
    );
  }

  /**
   * Handle HTTP errors.
   * Mirrors the implementation in UserStoryService.
   * @param error - The HTTP error response
   * @returns An observable that throws a user-facing error message.
   */
  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'An error occurred in the persistence service. Please try again later.';

    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      errorMessage = `Network error: ${error.error.message}`;
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      console.error(
        `Backend returned code ${error.status}, body was: `, error.error);

      // Use detail field from FastAPI HTTPException if available
      const detail = error.error?.detail;

      switch (error.status) {
        case 400: // Bad Request
          errorMessage = detail || 'Invalid request. Please check your input.';
          break;
        case 401: // Unauthorized
          errorMessage = detail || 'Unauthorized. Please log in again.';
          // Optionally trigger re-authentication flow here
          // this.authService.logout(); // Example
          break;
        case 403: // Forbidden
          errorMessage = detail || 'Access denied. You do not have permission.';
          break;
        case 404: // Not Found
          errorMessage = detail || 'The requested resource was not found.';
          break;
        case 409: // Conflict
          errorMessage = detail || 'Conflict: The resource may already exist or cannot be created/updated.';
          break;
        case 500: // Internal Server Error
          errorMessage = detail || 'Server error. Please contact support or try again later.';
          break;
        default:
          errorMessage = `Unexpected error: ${error.status} - ${detail || error.message || error.statusText}`;
      }
    }

    console.error('Error in PersistenceApiService:', errorMessage, error);
    // Return an observable with a user-facing error message.
    return throwError(new Error(errorMessage));
  }

  // ── Story Debug Log ──

  getStoryDebugLog(storyId: string): Observable<{ story_id: string; project_id: string; story_text: string; ambiguity_ids: string[]; created_at: string; session_name: string; user_name: string }> {
    return this.getHeaders().pipe(
      switchMap((headers) => {
        const url = `${this.apiUrl}/story-debug-log/${storyId}`;
        return this.http.get<any>(url, { headers });
      }),
      catchError(this.handleError)
    );
  }
}
